import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * HeroCard — the shared "premium hero" surface (b13g G1/G2).
 *
 * Restores the OLD winning event-card look the trainer flagged as the gold
 * standard (IMG_8805): a full-bleed artwork band across the top of the card,
 * a status badge + type pill below it, a large wrap-friendly title, and a
 * footer line (countdown + secondary link). The whole card carries a soft
 * colour glow driven by a single accent colour so the event/raid type reads
 * at a glance from the border, NOT from a tiny pill.
 *
 * One component, two surfaces:
 *   - Calendar uses it for Today's Focus and every Upcoming event row.
 *   - Profile uses it for the Raid Radar rows, the Priority Dex hero frame,
 *     and the Priorities Radar wrapper.
 *
 * The card body uses `--surface-card` (b13f) so it flips correctly in
 * light/dark. The glow colour is passed in as a CSS colour string — callers
 * map their domain (event category, raid type) to a `--event-color-*` /
 * `--accent` token and hand it here, keeping HeroCard domain-agnostic.
 */

export interface HeroCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Resolved CSS colour for the glow border (e.g. `var(--event-color-max)`). */
  glowColor?: string;
  /** Strength of the glow ring. `soft` for subtle framing, `bold` for hero. */
  glow?: "none" | "soft" | "bold";
  /** Full-bleed media slot painted across the top of the card. */
  media?: React.ReactNode;
  /** Footer slot (countdown + secondary link). Rendered with a divider. */
  footer?: React.ReactNode;
  /** When true the whole card is interactive (adds press affordance). */
  interactive?: boolean;
}

const GLOW_ALPHA: Record<NonNullable<HeroCardProps["glow"]>, string> = {
  none: "0%",
  soft: "22%",
  bold: "42%",
};

const SHADOW_ALPHA: Record<NonNullable<HeroCardProps["glow"]>, string> = {
  none: "0%",
  soft: "14%",
  bold: "26%",
};

export const HeroCard = React.forwardRef<HTMLDivElement, HeroCardProps>(
  (
    {
      className,
      glowColor = "var(--accent-fill)",
      glow = "soft",
      media,
      footer,
      interactive = false,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const ringColor =
      glow === "none"
        ? "var(--border-subtle)"
        : `color-mix(in srgb, ${glowColor} ${GLOW_ALPHA[glow]}, var(--border-subtle))`;
    const shadow =
      glow === "none"
        ? "var(--shadow-sm)"
        : `0 8px 32px color-mix(in srgb, ${glowColor} ${SHADOW_ALPHA[glow]}, transparent)`;

    return (
      <div
        ref={ref}
        data-hero-card=""
        className={cn(
          "relative overflow-hidden rounded-[var(--radius-lg)]",
          "bg-[var(--surface-card)] text-[var(--text-primary)]",
          "border transition-all",
          interactive && "hover-elevate active-elevate-2",
          className,
        )}
        style={{
          borderColor: ringColor,
          boxShadow: shadow,
          ...style,
        }}
        {...props}
      >
        {media && (
          <div className="hero-card__media relative w-full overflow-hidden">
            {media}
            {/* Scrim so an overlaid badge stays legible on bright artwork. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2"
              style={{
                background:
                  "linear-gradient(to top, var(--surface-card) 2%, transparent)",
              }}
            />
          </div>
        )}
        <div className="hero-card__body p-4">{children}</div>
        {footer && (
          <div className="hero-card__footer px-4 pb-4 -mt-1">{footer}</div>
        )}
      </div>
    );
  },
);
HeroCard.displayName = "HeroCard";

/**
 * HeroCardMedia — convenience full-bleed image with the OLD ~50%-height
 * crop and a graceful fallback when the artwork is missing.
 */
export function HeroCardMedia({
  src,
  alt,
  className,
  fallback,
  testId,
}: {
  src?: string;
  alt: string;
  className?: string;
  fallback?: React.ReactNode;
  testId?: string;
}) {
  const [failed, setFailed] = React.useState(false);
  if (!src || failed) {
    return (
      <div
        data-testid={testId}
        aria-hidden
        className={cn(
          "flex items-center justify-center w-full",
          "bg-[var(--accent-subtle)]",
          className,
        )}
      >
        {fallback}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      draggable={false}
      onError={() => setFailed(true)}
      data-testid={testId}
      className={cn("w-full object-cover", className)}
    />
  );
}
